import { createSelector } from "reselect";
import Transcript from "../../models/Transcript";
import clamp from "../../common/helpers/clamp";
import { getCNVTranscripts } from "./CNVs.selectors";
import {
  getVariantResult,
  getVariantPos,
  getQueryType,
} from "./general.selectors";

const minRange = 50;
const maxRange = 260000000; // bigger than all chrs

const getPrimeTranscript = createSelector(getVariantResult, (res) => {
  if (!res) return null;
  const { chromosome, transcripts, mainTranscript } = res;
  const main = mainTranscript ? new Transcript(mainTranscript) : null;
  const prioritizedTranscript = (transcripts || [])
    .filter(Boolean)
    .filter((x) => x?.chromo === chromosome)
    .map((x) => new Transcript(x, null, true))
    .sort((a, b) => a.compareTo(b))
    .shift();
  return prioritizedTranscript || main;
});

const getPrimeTranscriptLength = createSelector(getPrimeTranscript, (tr) => {
  return tr?.baseLength || 0;
});

const getRefLength = createSelector(
  getVariantResult,
  (res) => res?.ref?.length || 0
);

const getCnvLength = createSelector(getVariantResult, (res) => {
  return res?.length || 0;
});

const getRangeLength = createSelector(
  getQueryType,
  getPrimeTranscriptLength,
  getRefLength,
  getCnvLength,
  (queryType, mainTranscriptLength, refLength, cnvLength) => {
    const isCnv = queryType === "cnv";
    const isGene = queryType === "gene";
    if (isGene) return mainTranscriptLength;
    if (isCnv) return cnvLength;
    return refLength;
  }
);

const getStart = createSelector(
  getVariantPos,
  getQueryType,
  getPrimeTranscript,
  (x0, queryType, transcript) => {
    const isGene = queryType === "gene";
    return isGene && transcript ? transcript.region.x0 : x0 || -1;
  }
);

const getEnd = createSelector(
  getStart,
  getRangeLength,
  (x0, length) => x0 + length
);
const getPosition = createSelector(getStart, getEnd, (start, end) => {
  const range = end - start;
  const middle = start + range * 0.5;
  const visRange = clamp(range, minRange, maxRange);
  const visStart = middle - visRange * 0.5;
  const visEnd = middle + visRange * 0.5;
  return { start, end, visStart, visEnd };
});

const getAcmgTranscript = createSelector(
  getVariantResult,
  getPrimeTranscript,
  (variantRes, primeTranscript) => {
    const { transcript: acmg } = variantRes?.acmg_annotation || {};
    const { name: prime } = primeTranscript || {};
    return acmg || prime;
  }
);

const getCnvTranscript = createSelector(getCNVTranscripts, (transcripts) => {
  return transcripts ? transcripts[0]?.transcript || null : null;
});

export { getPrimeTranscript, getPosition, getAcmgTranscript, getCnvTranscript };
